import {forwardRef} from 'react';
import cx from 'classnames';

import {TextComponent, TextProps} from './Text.types';

import {textStyles} from './Text.css';

/**
 * The Text component is used to present text content with specific,
 * design-defined typographic styles and scales.
 *
 * By default, it will render as an inline `<span>` element, though this can
 * be overridden by providing a valid elementType via the `as` prop.
 *
 * Text accepts a `variant` prop, which may be used as a shorthand for
 * rendering text with commonly used combined sets of styles (eg. font-sizes,
 * weights, families, text-transforms, etc) for different text scales (eg.
 * for various heading levels or body copy types).
 */
export const Text: TextComponent = forwardRef(
  <T extends React.ElementType = 'span'>(
    {
      as,
      className,
      clamp,
      color = 'black',
      fontWeight,
      style,
      textAlign,
      transform,
      variant = 'body',
      ...otherProps
    }: TextProps<T>,
    ref?: PolymorphicRef<T>,
  ) => {
    const Component = as || 'span';

    return (
      <Component
        className={cx(
          textStyles({color, clamp: !!clamp, fontWeight, textAlign, transform, variant}),
          className,
        )}
        ref={ref}
        style={clamp ? {...style, WebkitLineClamp: clamp} : style}
        {...otherProps}
      />
    );
  },
);
